/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * INT-02 — Extend warranty PLI helpers for the storefront layer.
 *
 * Warranty PLIs (`c_isProtectionPlan === true`) live in the basket alongside
 * the merchandise PLIs they cover, but the legacy SFRA storefront treats them
 * as accounting-only line items: they are HIDDEN from the cart's line item
 * list and their cost is REMOVED from the displayed merchandise subtotal,
 * surfacing instead in the dedicated "PROTECTION PLAN(S)" order-summary row.
 *
 * This module mirrors that legacy behaviour from a single place so cart,
 * checkout, mobile heading, and any future surfaces share the exact same
 * filtering / aggregation rules. Without it, each consumer reinvents the
 * `c_isProtectionPlan` predicate (already happening — see `_app.cart.tsx`
 * and `cart-content.tsx`) and risks drifting on what "the protection-plan
 * total" actually means.
 *
 * Legacy references:
 *   - cart-list-items.isml — `!item.object.product.custom.isProtectionPlan`
 *     filters warranty PLIs out of the cart list view.
 *   - ordertotals.isml — `adjSubTotal.subtract(pdict.totalPPCost)` removes
 *     the warranty cost from the displayed subtotal so it only appears in
 *     the protection-plan section.
 */

import type { ShopperBasketsV2, ShopperOrders } from '@/scapi';

import { isReturnPliItem } from '@/lib/mattress-takeback/return-pli';

/** Basket or Order — both expose the same `productItems` shape. */
export type ExtendBasketLike = Pick<
    ShopperBasketsV2.schemas['Basket'] | ShopperOrders.schemas['Order'],
    'productItems'
> & {
    /** Mirrored by `int_sfnext_extend/ExtendBasketEnricher` as the sum of
     *  warranty PLI `c_ppCost` values, expressed in the basket currency's
     *  main unit (dollars for USD). Optional — only present on basket
     *  responses (the `basket.modifyGETResponse` hook), not on order
     *  responses (which carry the materialized warranty PLI prices instead).
     *  Cast through `Record<string, unknown>` to avoid a hard dependency on
     *  the SCAPI schema declaring `c_totalPPCost`. */
    c_totalPPCost?: number;
};

/**
 * Custom-field projection for warranty PLIs. Each warranty PLI carries:
 *   - `c_isProtectionPlan` — set to `true` by the enricher; the canonical
 *      flag the storefront uses to identify a warranty PLI.
 *   - `c_ppCost` — the per-line warranty cost (dollars). Sum across warranty
 *      PLIs to derive the basket-level protection-plan total when the
 *      basket-level `c_totalPPCost` isn't on the response (e.g. on order
 *      responses, which don't go through the basket enricher).
 */
type WarrantyItemExtras = {
    c_isProtectionPlan?: boolean;
    cIsProtectionPlan?: boolean;
    c_ppCost?: number;
    productId?: string | null;
    productName?: string;
    priceAfterOrderDiscount?: number;
    priceAfterItemDiscount?: number;
    price?: number;
    basePrice?: number;
    priceAdjustments?: Array<{ price?: number; itemText?: string; calloutMsg?: string }>;
};

type ProductItem = ShopperBasketsV2.schemas['ProductItem'];

/**
 * Warranty-SKU prefix pattern. The Extend catalog groups every plan SKU under
 * one of three families keyed off the product-type slug:
 *   - `FURNID-*`   — furniture warranties (e.g. `FURNID-5Y`)
 *   - `FURMATT-*`  — mattress warranties (e.g. `FURMATT-10Y`)
 *   - `FURNOD-*`   — outdoor warranties  (e.g. `FURNOD-5Y`)
 *
 * These are the ONLY SKU families sold as plans, so a match is a hard signal
 * of "this is an Extend warranty PLI" even when the enrichment flag is
 * missing (the order-confirmation SCAPI response is the primary case:
 * `c_isProtectionPlan` is written by `ExtendBasketEnricher` in the
 * `basket.modifyGETResponse` hook, but that hook does NOT fire on the
 * `order.get` path, so warranty rows on order responses only carry the
 * warranty SKU — no plan flag).
 *
 * The `i` flag is defensive: legacy imports occasionally stored the master
 * SKU lower-cased, and the shopper never sees these strings directly.
 */
const WARRANTY_SKU_PATTERN = /^(FURNID|FURMATT|FURNOD)-/i;

/**
 * True when the given basket product item represents a warranty PLI.
 *
 * Resolution order:
 *   1. `c_isProtectionPlan === true` (or camelCase `cIsProtectionPlan`) — the
 *      canonical flag written by `int_sfnext_extend/ExtendBasketEnricher` on
 *      every basket-GET response. This is authoritative wherever the enricher
 *      has run.
 *   2. `productId` matches `WARRANTY_SKU_PATTERN` — safety net for surfaces
 *      that never see the enricher (chiefly the order-confirmation page's
 *      `order.get` response).
 *
 * NOTE (SSF-413 follow-up): earlier revisions also treated a non-empty
 * `parentItemId` / `c_parentItemId` as a warranty signal. That was correct
 * for warranty PLIs — they link to the merchandise line they cover — but
 * it also matched **kit component PLIs**, which use `parentItemId` to
 * point at their kit master. That overreach silently filtered every kit
 * component out of `getMerchandiseAvailabilityLines`, which in turn made
 * every checkout-blocking availability gate ignore kit-component
 * unavailability. The SKU-prefix rule is the correct scoped safety net:
 * every warranty SKU matches; no merchandise / kit-component SKU does.
 *
 * Reads through a typed projection rather than `as any` so a future schema
 * regeneration that adds `c_isProtectionPlan` to the canonical SCAPI types
 * keeps compiling. Defensive on undefined / null inputs because some callers
 * stream items through map/filter chains where intermediate undefined items
 * are possible.
 */
export function isProtectionPlanItem(item: ProductItem | undefined | null): boolean {
    if (!item) return false;
    const planItem = item as WarrantyItemExtras;
    if (planItem.c_isProtectionPlan === true || planItem.cIsProtectionPlan === true) return true;
    if (typeof planItem.productId === 'string' && WARRANTY_SKU_PATTERN.test(planItem.productId)) return true;
    return false;
}

/**
 * Return only the merchandise (non-warranty, non-take-back-return) product
 * items from a basket / order. Used by the cart line item list, mobile
 * heading item count, checkout order-review item list, and order
 * confirmation summary — anywhere we want to show the customer "what they
 * actually bought" rather than internal bookkeeping records.
 *
 * Two families of non-merchandise PLIs are filtered:
 *   1. Extend warranty PLIs (`isProtectionPlanItem`) — their cost is
 *      surfaced in the dedicated PROTECTION PLAN(S) order-summary row.
 *   2. Mattress take-back `*RETURN` PLIs (`isReturnPliItem`) — $0
 *      D365-integration records that carry no shopper-facing meaning.
 *      Legacy SFRA filtered them out of every cart / mini-cart / checkout
 *      / confirmation template; the same guarantee is preserved here so
 *      cart, checkout summary, and order confirmation never render a
 *      "Product · *RETURN · $0.00" row next to the merchandise the
 *      shopper actually paid for. Server-side, the cartridge hook
 *      `BasketResponseEnricher.filterReturnPliFromResponse` also strips
 *      the RETURN PLI from the SCAPI basket-GET response — this
 *      client-side filter is the second line of defense, and the only
 *      line of defense for the order-confirmation `order.get` path
 *      (which does not fire the basket-response enricher hook).
 *
 * Returns the original array reference when nothing is filtered so
 * downstream `useMemo` / referential-equality optimisations don't see a
 * spurious new array on every render of a warranty-free / return-free
 * basket.
 */
export function getMerchandiseProductItems<T extends ProductItem>(productItems: T[] | undefined): T[] {
    if (!productItems || productItems.length === 0) return productItems ?? [];
    const filtered = productItems.filter((item) => !isProtectionPlanItem(item) && !isReturnPliItem(item));
    return filtered.length === productItems.length ? productItems : filtered;
}

/**
 * Total cost of all warranty PLIs in the basket, in the basket currency's
 * main unit (dollars for USD).
 *
 * Resolution order:
 *   1. `basket.c_totalPPCost` — pre-computed by `ExtendBasketEnricher` for
 *      basket responses. Fast path: one field read instead of a list scan.
 *   2. Sum of warranty amounts across protection-plan product items. Prefer
 *      `c_ppCost` (basket enricher output), then fall back to order line
 *      amount fields (`priceAfterOrderDiscount`, `priceAfterItemDiscount`,
 *      `price`, `basePrice`), and finally adjustment totals when direct line
 *      prices are zero.
 *   3. `0` — no warranty PLIs present.
 *
 * Always returns a finite non-negative number so arithmetic with it is safe.
 */
export function getProtectionPlanTotal(basket: ExtendBasketLike | undefined): number {
    if (!basket) return 0;
    if (typeof basket.c_totalPPCost === 'number' && Number.isFinite(basket.c_totalPPCost)) {
        return basket.c_totalPPCost;
    }
    const readFiniteAmount = (value: unknown): number | undefined =>
        typeof value === 'number' && Number.isFinite(value) ? value : undefined;

    let total = 0;
    for (const item of basket.productItems ?? []) {
        if (!isProtectionPlanItem(item)) continue;
        const planItem = item as WarrantyItemExtras;
        const directCost = [
            planItem.c_ppCost,
            planItem.priceAfterOrderDiscount,
            planItem.priceAfterItemDiscount,
            planItem.price,
            planItem.basePrice,
        ]
            .map(readFiniteAmount)
            .find((value): value is number => value !== undefined);

        if (directCost !== undefined && directCost !== 0) {
            total += directCost;
            continue;
        }

        const adjustmentCost = (planItem.priceAdjustments ?? []).reduce((sum, adjustment) => {
            const value = readFiniteAmount(adjustment.price);
            return value !== undefined ? sum + value : sum;
        }, 0);
        if (Number.isFinite(adjustmentCost) && adjustmentCost !== 0) {
            total += adjustmentCost;
        }
    }
    return total;
}
